import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import { Hourglass } from "react-loader-spinner";
import { useNavigate, useParams } from "react-router-dom";
import ModalDeleteFile from "../../modal/modalDeleteFile";
import ImagePreviewModal from "../../modal/ImagePreviewModal";
import FileIcon from "./FileIcon";
import useFileManagerApi from "../../../hooks/api/useFileManagerApi";

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'heic'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'avi', 'mkv', 'webm', 'wmv', 'flv'];
const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'];

const ContentFiles = ({
                        selectedFolders = [],
                        handleCheckboxChange = () => {},
                        sortBy = "Name",
                        folders = [],
                        files = [],
                        refreshFolderStructure = () => {},
                        onFolderClick = null, // Optional: for modal navigation
                        currentPath = null, // Optional: for modal path
                        rootStructure = {},
                      }) => {
  const [loading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [viewMode, setViewMode] = useState('icons'); // 'icons' | 'list' | 'columns'
  const [expandedFolders, setExpandedFolders] = useState(new Set()); // Track expanded folders in list mode
  const navigate = useNavigate();
  const params = useParams();
  const dropdownRef = useRef(null);
  const { requestDownloadLink, deleteFiles } = useFileManagerApi();
  
  // Helper function to normalize item identifier (same logic as modalAttachFiles)
  const normalizeItemId = (item) => {
    if (!item) return "";
    const identifier = item.path || item.fullPath || item.id || item.name || "";
    return identifier.replace(/^\/+/, "").replace(/\/+$/, "");
  };
  
  const selectedIds = Array.isArray(selectedFolders)
    ? selectedFolders.map((entry) => {
        if (typeof entry === "string") {
          return entry.replace(/^\/+/, "").replace(/\/+$/, "");
        }
        const identifier = entry.path || entry.fullPath || entry.id || entry.name || "";
        return identifier.replace(/^\/+/, "").replace(/\/+$/, "");
      })
    : [];

 
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };

    if (dropdownOpen !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      const month = months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const time = `${hours}:${minutes}`;
      
      return `${month} ${day} ${year} ${time}`;
    } catch (error) {
      return '-';
    }
  };

  const formatDateMobile = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}.${month}.${year}`;
    } catch (error) {
      return '';
    }
  };

  const formatFileSize = (sizeInBytes) => {
    if (!sizeInBytes || sizeInBytes === 0) return '-';
    
    const sizeInMB = sizeInBytes / (1024 * 1024);
    return `${Math.round(sizeInMB * 100) / 100} MB`;
  };

  const handleDropdownToggle = (itemId, e) => {
    e.stopPropagation();
    setDropdownOpen(dropdownOpen === itemId ? null : itemId);
  };

  const handleDeleteFolder = (folder, e) => {
    e.stopPropagation();
    setSelectedItem(folder);
    setDeleteModalOpen(true);
    setDropdownOpen(null);
  };

  const handleDelete = (file, e) => {
    e.stopPropagation();
    setSelectedItem(file);
    setDeleteModalOpen(true);
    setDropdownOpen(null);
  };

  const handleDownload = async (item, e) => {
    e.stopPropagation();
    setDropdownOpen(null);
    
    try {
      const isFolder = item.type === "folder" || item.itemType === "folder";
      toast.info(`Preparing download for ${item.name}...`);
      
      // Build the full path for the file or folder
      const currentPath = (params["*"] || "").trim();
      let itemPath;
      
      if (isFolder) {
        // For folders, use folder.id or folder.name
        const folderName = item.id ? item.id.replace(/\/+$/, '') : item.name;
        itemPath = currentPath 
          ? `${currentPath}/${folderName}`
          : folderName;
      } else {
        // For files, use item.name
        itemPath = currentPath 
          ? `${currentPath}/${item.name}`
          : item.name;
      }
      
      // Make GET request to get presigned URL
      let normalizedPath = itemPath.startsWith("/") ? itemPath : `/${itemPath}`;

      if (isFolder) {
        if (!normalizedPath.endsWith("/")) {
          normalizedPath += "/";
        }
      } else {
        normalizedPath = normalizedPath.replace(/\/+$/, "");
      }

      const response = await requestDownloadLink(normalizedPath);

      if (response.data.success && response.data.presignedUrl) {
        // Open presigned URL in new tab to download the file/folder
        window.open(response.data.presignedUrl, '_blank');
        toast.success(`Downloading ${item.name}...`);
      } else {
        toast.error(response.data.error || "Error preparing download");
      }
    } catch (error) {
      console.error("Error downloading item:", error);
      toast.error(error.response?.data?.error || "Error downloading item");
    }
  };

  const getFileCategory = (fileName = "") => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
    if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
    if (DOCUMENT_EXTENSIONS.includes(extension)) return 'document';
    return 'other';
  };

  const tryOpenPreview = (item) => {
    if (!item) return;
    const category = getFileCategory(item.name || "");
    if (["image", "video", "document"].includes(category) && (item.previewUrl || item.path || item.fullPath || item.id)) {
      setPreviewImage({
        ...item,
        previewCategory: category,
        previewPath: item.previewPath || item.path || item.fullPath || item.id || item.name,
      });
    }
  };

  const handleItemClick = (item) => {
    if (item.itemType === 'folder' || item.type === "folder") {
      // Always navigate to folder content in list mode
      handleNavigateFolderContent(item);
    } else {
      tryOpenPreview(item);
    }
  };

  const handleToggleFolderExpand = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // Get files and folders from expanded folders
  const getFilesFromFolder = (folder) => {
    if (!folder.structure) return [];
    
    return Object.entries(folder.structure)
      .filter(([key, value]) => {
        // Skip metadata properties
        if (key === 'isFolder' || key === 'hasFiles' || key === 'path' || key === 'lastModified' || key === 'size' || key === 'etag') {
          return false;
        }
        // Include both files and folders
        return value && typeof value === 'object' && (value.isFolder === true || value.isFolder === false);
      })
      .map(([name, value]) => {
        const sanitizedName = name.replace(/\/+$/, '');
        const isFolder = value.isFolder === true;
        
        if (isFolder) {
          // Check if folder has files inside
          const hasFiles = Object.keys(value).some(
            key => key !== 'isFolder' && key !== 'hasFiles' && key !== 'path' && key !== 'lastModified' && key !== 'size' && key !== 'etag' && 
            value[key] && typeof value[key] === 'object' && value[key].isFolder !== true
          );
          
          const fullPath = value?.path || `${folder.path || folder.fullPath || folder.id}/${sanitizedName}/`;

          return {
            id: `${folder.id}/${sanitizedName}`,
            name: sanitizedName,
            itemType: 'folder',
            type: 'folder',
            size: value?.size ? parseInt(value.size, 10) : 0,
            date: value?.lastModified || "",
            folderPath: folder.id,
            structure: value,
            hasFiles: hasFiles,
            isChildItem: true,
            path: fullPath,
            fullPath,
          };
        } else {
          // It's a file
          const extension = sanitizedName.split('.').pop()?.toLowerCase() || '';
          const isImage = IMAGE_EXTENSIONS.includes(extension);
          
          const fullPath = value?.path || `${folder.path || folder.fullPath || folder.id}/${sanitizedName}`;

          return {
            id: `${folder.id}/${sanitizedName}`,
            name: sanitizedName,
            itemType: 'file',
            type: isImage ? "image" : "file",
            size: value?.size ? parseInt(value.size, 10) : 0,
            date: value?.lastModified || "",
            previewUrl: sanitizedName,
            folderPath: folder.id,
            isChildItem: true,
            path: fullPath,
            fullPath,
          };
        }
      });
  };

  // Helper function to render items in list mode
  const formatItemTypeLabel = (item) => {
    if (!item) return "file";
    const sanitizedName = (item.name || "").replace(/\/+$/, "");
    const extension = sanitizedName.split('.').pop()?.toLowerCase() || '';
    
    if (item.itemType === 'folder' || item.type === 'folder') {
      return 'folder';
    }

    if (item.type && item.type !== 'file') {
      return item.type.toString().toLowerCase();
    }

    const category = getFileCategory(sanitizedName);
    if (category === 'image') return 'image';
    if (category === 'video') return extension || 'video';
    if (category === 'document') return extension || 'document';

    return extension || 'file';
  };

  const renderNestedItemsList = (items, indentLevel = 0) => {
    return items.map((item) => {
      const isItemFolder = item.itemType === 'folder' || item.type === 'folder';
      const displayType = formatItemTypeLabel(item);
      
      return (
        <tr
          key={item.id}
          className={`
            border-b border-gray-100 hover:bg-gray-50 cursor-pointer w-full bg-white
            ${selectedIds.includes(normalizeItemId(item)) ? "bg-blue-50" : ""}
          `}
          onClick={() => handleItemClick(item)}
        >
          <td className="px-4 py-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                className="rounded cursor-pointer border-gray-400 w-4 h-4"
                checked={selectedIds.includes(normalizeItemId(item))}
                onChange={(e) => {
                  e.stopPropagation();
                  handleCheckboxChange(item, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center">
                  {isItemFolder
                    ? getFolderIcon(item, "w-[25px] h-[22px]")
                    : getFileIcon(item.name, item.type, "w-[25px] h-[22px]")}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-bodyMR text-dark3 font-medium truncate max-w-[400px]">{item.name}</span>
                </div>
              </div>
            </div>
          </td>
          <td className="px-4 py-3 text-bodyESM text-dark5 capitalize">
            {displayType}
          </td>
          <td className="px-4 py-3 text-bodyESM text-dark5">
            {formatDate(item.date)}
          </td>
          <td className="px-4 py-3 text-bodyESM text-dark5">
            {isItemFolder ? '-' : formatFileSize(item.size)}
          </td>
          <td className="px-4 py-3 text-right">
            <div className="flex items-center justify-end relative">
              {/* Three dots dropdown menu */}
              <button
                className="text-dark4 hover:text-primary p-1 rounded transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDropdownToggle(item.id, e);
                }}
                title="More options"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>

              {dropdownOpen === item.id && (
                <div
                  ref={dropdownRef}
                  className="absolute right-0 mt-2 w-44 desktop:w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isItemFolder ? (
                    <>
                      <button
                        className="w-full text-dark4 mobile:text-bodySR text-left px-3 mobile:px-2 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-t-lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(item, e);
                          setDropdownOpen(null);
                        }}
                      >
                        <svg width="18" height="16" viewBox="0 0 22 18" fill="currentColor">
                          <path fillRule="evenodd" clipRule="evenodd" d="M18.5 18C19.2956 18 20.0587 17.6839 20.6213 17.1213C21.1839 16.5587 21.5 15.7956 21.5 15V6C21.5 5.20435 21.1839 4.44129 20.6213 3.87868C20.0587 3.31607 19.2956 3 18.5 3H13.121C12.9222 2.99983 12.7315 2.92069 12.591 2.78L10.47 0.66C10.2611 0.450888 10.0131 0.284971 9.74016 0.171727C9.46717 0.0584821 9.17454 0.000128741 8.879 0H3.5C2.70435 0 1.94129 0.316071 1.37868 0.87868C0.816071 1.44129 0.5 2.20435 0.5 3V15C0.5 15.7956 0.816071 16.5587 1.37868 17.1213C1.94129 17.6839 2.70435 18 3.5 18H18.5ZM11.75 7.5C11.75 7.30109 11.671 7.11032 11.5303 6.96967C11.3897 6.82902 11.1989 6.75 11 6.75C10.8011 6.75 10.6103 6.82902 10.4697 6.96967C10.329 7.11032 10.25 7.30109 10.25 7.5V11.69L8.53 9.97C8.38783 9.83752 8.19978 9.7654 8.00548 9.76882C7.81118 9.77225 7.62579 9.85097 7.48838 9.98838C7.35097 10.1258 7.27225 10.3112 7.26882 10.5055C7.2654 10.6998 7.33752 10.8878 7.47 11.03L10.47 14.03C10.6106 14.1705 10.8012 14.2493 11 14.2493C11.1988 14.2493 11.3894 14.1705 11.53 14.03L14.53 11.03C14.6037 10.9613 14.6628 10.8785 14.7038 10.7865C14.7448 10.6945 14.7668 10.5952 14.7686 10.4945C14.7704 10.3938 14.7518 10.2938 14.7141 10.2004C14.6764 10.107 14.6203 10.0222 14.549 9.95096C14.4778 9.87974 14.393 9.8236 14.2996 9.78588C14.2062 9.74816 14.1062 9.72963 14.0055 9.73141C13.9048 9.73318 13.8055 9.75523 13.7135 9.79622C13.6215 9.83721 13.5387 9.89631 13.47 9.97L11.75 11.69V7.5Z" fill="currentColor"/>
                        </svg>
                        <span>Download this folder</span>
                      </button>
                      <button
                        className="w-full text-dark4 mobile:text-bodySR text-left px-3 mobile:px-2 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-b-lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFolder(item, e);
                          setDropdownOpen(null);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                          <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                        </svg>
                        <span>Delete this folder</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="w-full text-dark4 text-left px-3 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-t-lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(item, e);
                          setDropdownOpen(null);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                          <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                        </svg>
                        <span>Download this file</span>
                      </button>
                      <button
                        className="w-full text-dark4 text-left px-3 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-b-lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item, e);
                          setDropdownOpen(null);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                          <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                        </svg>
                        <span>Delete this file</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    });
  };

  // Render items for mobile list mode (card-based design)
  const renderNestedItemsListMobile = (items, indentLevel = 0) => {
    return items.map((item) => {
      const isItemFolder = item.itemType === 'folder' || item.type === 'folder';
      const dateFormatted = formatDateMobile(item.date);
      
      return (
        <div
          key={item.id}
          className={`
            flex items-center gap-3 px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 relative
            ${selectedIds.includes(normalizeItemId(item)) ? "bg-blue-50" : ""}
          `}
          onClick={() => handleItemClick(item)}
        >
          {/* Checkbox */}
          <input
            type="checkbox"
            className="rounded cursor-pointer border-gray-400 w-4 h-4 flex-shrink-0"
            checked={selectedIds.includes(normalizeItemId(item))}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(item, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Folder/File Icon */}
          <div className="flex-shrink-0">
            {isItemFolder
              ? getFolderIcon(item, "w-8 h-8")
              : getFileIcon(item.name, item.type, "w-8 h-8")}
          </div>

          {/* Name and Date */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-bodyMR text-dark3 font-medium truncate">{item.name}</span>
            </div>
            {dateFormatted && (
              <div className="flex items-center gap-1">
                <span className="text-bodyESR text-dark6">Modified {dateFormatted}</span>
              </div>
            )}
          </div>

          {/* Options Menu (Three Dots) */}
          <div className="flex-shrink-0 relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDropdownToggle(item.id, e);
              }}
              className="text-dark4 hover:text-primary p-1 rounded transition-colors"
              title="More options"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {dropdownOpen === item.id && (
              <div
                ref={dropdownRef}
                className="absolute right-0 mt-2 w-44 desktop:w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                onClick={(e) => e.stopPropagation()}
              >
                {isItemFolder ? (
                  <>
                    <button
                      className="w-full text-dark4 mobile:text-bodySR text-left px-3 mobile:px-2 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-t-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(item, e);
                        setDropdownOpen(null);
                      }}
                    >
                      <svg width="18" height="16" viewBox="0 0 22 18" fill="currentColor">
                        <path fillRule="evenodd" clipRule="evenodd" d="M18.5 18C19.2956 18 20.0587 17.6839 20.6213 17.1213C21.1839 16.5587 21.5 15.7956 21.5 15V6C21.5 5.20435 21.1839 4.44129 20.6213 3.87868C20.0587 3.31607 19.2956 3 18.5 3H13.121C12.9222 2.99983 12.7315 2.92069 12.591 2.78L10.47 0.66C10.2611 0.450888 10.0131 0.284971 9.74016 0.171727C9.46717 0.0584821 9.17454 0.000128741 8.879 0H3.5C2.70435 0 1.94129 0.316071 1.37868 0.87868C0.816071 1.44129 0.5 2.20435 0.5 3V15C0.5 15.7956 0.816071 16.5587 1.37868 17.1213C1.94129 17.6839 2.70435 18 3.5 18H18.5ZM11.75 7.5C11.75 7.30109 11.671 7.11032 11.5303 6.96967C11.3897 6.82902 11.1989 6.75 11 6.75C10.8011 6.75 10.6103 6.82902 10.4697 6.96967C10.329 7.11032 10.25 7.30109 10.25 7.5V11.69L8.53 9.97C8.38783 9.83752 8.19978 9.7654 8.00548 9.76882C7.81118 9.77225 7.62579 9.85097 7.48838 9.98838C7.35097 10.1258 7.27225 10.3112 7.26882 10.5055C7.2654 10.6998 7.33752 10.8878 7.47 11.03L10.47 14.03C10.6106 14.1705 10.8012 14.2493 11 14.2493C11.1988 14.2493 11.3894 14.1705 11.53 14.03L14.53 11.03C14.6037 10.9613 14.6628 10.8785 14.7038 10.7865C14.7448 10.6945 14.7668 10.5952 14.7686 10.4945C14.7704 10.3938 14.7518 10.2938 14.7141 10.2004C14.6764 10.107 14.6203 10.0222 14.549 9.95096C14.4778 9.87974 14.393 9.8236 14.2996 9.78588C14.2062 9.74816 14.1062 9.72963 14.0055 9.73141C13.9048 9.73318 13.8055 9.75523 13.7135 9.79622C13.6215 9.83721 13.5387 9.89631 13.47 9.97L11.75 11.69V7.5Z" fill="currentColor"/>
                      </svg>
                      <span>Download this folder</span>
                    </button>
                    <button
                      className="w-full text-dark4 mobile:text-bodySR text-left px-3 mobile:px-2 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-b-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(item, e);
                        setDropdownOpen(null);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                      </svg>
                      <span>Delete this folder</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="w-full text-dark4 text-left px-3 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-t-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(item, e);
                        setDropdownOpen(null);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                      </svg>
                      <span>Download this file</span>
                    </button>
                    <button
                      className="w-full text-dark4 text-left px-3 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-b-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item, e);
                        setDropdownOpen(null);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                      </svg>
                      <span>Delete this file</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  // Recursive helper function to render nested items in icons mode
  const renderNestedItemsIcons = (items, indentLevel = 0) => {
    return items.map((item) => {
      const isItemFolder = item.itemType === 'folder' || item.type === 'folder';
      const isExpanded = isItemFolder && expandedFolders.has(item.id);
      const childItems = isItemFolder && isExpanded && item.hasFiles ? getFilesFromFolder(item) : [];
      
      return (
        <React.Fragment key={item.id}>
          <div
            className={`
              relative flex flex-col items-center p-4 rounded-lg cursor-pointer
              transition-all duration-200 group
              ${selectedIds.includes(normalizeItemId(item)) ? "bg-blue-50 ring-2 ring-primary" : "bg-white hover:shadow-xl"}
            `}
            style={{ marginLeft: `${indentLevel * 1.5}rem` }}
            onClick={() => handleItemClick(item)}
          >
            {/* Checkbox */}
            <input
              type="checkbox"
              className={`
                absolute top-2 left-2 rounded-3xl cursor-pointer border-gray-400
                transition-opacity duration-200 mobile:w-4 mobile:h-4 w-5 h-5 border-c
                ${selectedIds.includes(normalizeItemId(item)) ? "opacity-100" : "opacity-1 group-hover:opacity-100"}
              `}
              checked={selectedIds.includes(normalizeItemId(item))}
              onChange={(e) => {
                e.stopPropagation();
                handleCheckboxChange(item, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            />

            {/* Three dots dropdown menu */}
            <div className="absolute top-2 right-2">
              <button
                className={`
                  text-dark4 hover:text-primary p-1 rounded
                  transition-opacity duration-200
                  ${dropdownOpen === item.id ? "opacity-100" : "opacity-1 group-hover:opacity-100"}
                `}
                onClick={(e) => handleDropdownToggle(item.id, e)}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <circle cx="10" cy="4" r="1.5"/>
                  <circle cx="10" cy="10" r="1.5"/>
                  <circle cx="10" cy="16" r="1.5"/>
                </svg>
              </button>

              {dropdownOpen === item.id && (
                <div ref={dropdownRef} className="absolute right-0 mt-2 w-44 desktop:w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  {isItemFolder ? (
                    <>
                      <button
                        className="w-full text-dark4 mobile:text-bodySR text-left px-3 mobile:px-2 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-t-lg "
                        onClick={(e) => handleDownload(item, e)}
                      >
                        <svg width="18" height="16" viewBox="0 0 22 18" fill="currentColor">
                          <path fillRule="evenodd" clipRule="evenodd" d="M18.5 18C19.2956 18 20.0587 17.6839 20.6213 17.1213C21.1839 16.5587 21.5 15.7956 21.5 15V6C21.5 5.20435 21.1839 4.44129 20.6213 3.87868C20.0587 3.31607 19.2956 3 18.5 3H13.121C12.9222 2.99983 12.7315 2.92069 12.591 2.78L10.47 0.66C10.2611 0.450888 10.0131 0.284971 9.74016 0.171727C9.46717 0.0584821 9.17454 0.000128741 8.879 0H3.5C2.70435 0 1.94129 0.316071 1.37868 0.87868C0.816071 1.44129 0.5 2.20435 0.5 3V15C0.5 15.7956 0.816071 16.5587 1.37868 17.1213C1.94129 17.6839 2.70435 18 3.5 18H18.5ZM11.75 7.5C11.75 7.30109 11.671 7.11032 11.5303 6.96967C11.3897 6.82902 11.1989 6.75 11 6.75C10.8011 6.75 10.6103 6.82902 10.4697 6.96967C10.329 7.11032 10.25 7.30109 10.25 7.5V11.69L8.53 9.97C8.38783 9.83752 8.19978 9.7654 8.00548 9.76882C7.81118 9.77225 7.62579 9.85097 7.48838 9.98838C7.35097 10.1258 7.27225 10.3112 7.26882 10.5055C7.2654 10.6998 7.33752 10.8878 7.47 11.03L10.47 14.03C10.6106 14.1705 10.8012 14.2493 11 14.2493C11.1988 14.2493 11.3894 14.1705 11.53 14.03L14.53 11.03C14.6037 10.9613 14.6628 10.8785 14.7038 10.7865C14.7448 10.6945 14.7668 10.5952 14.7686 10.4945C14.7704 10.3938 14.7518 10.2938 14.7141 10.2004C14.6764 10.107 14.6203 10.0222 14.549 9.95096C14.4778 9.87974 14.393 9.8236 14.2996 9.78588C14.2062 9.74816 14.1062 9.72963 14.0055 9.73141C13.9048 9.73318 13.8055 9.75523 13.7135 9.79622C13.6215 9.83721 13.5387 9.89631 13.47 9.97L11.75 11.69V7.5Z" fill="currentColor"/>
                        </svg>
                        <span>Download this folder</span>
                      </button>
                      <button
                        className="w-full text-dark4 mobile:text-bodySR text-left px-3 mobile:px-2 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-b-lg"
                        onClick={(e) => handleDeleteFolder(item, e)}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                        </svg>
                        <span>Delete this folder</span>
                      </button>
                    </>
                  ) : (
                    <>
                      {item.type === "image" && (
                      <button
                        className="w-full text-dark4 text-left px-3 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-t-lg"
                        onClick={(e) => handleDownload(item, e)}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                          <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                        </svg>
                        <span>Download this file</span>
                      </button>
                      )}
                      <button
                        className="w-full text-dark4 text-left px-3 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-b-lg"
                        onClick={(e) => handleDelete(item, e)}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                          <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                        </svg>
                        <span>Delete this file</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Icon */}
            <div className="mb-3 mobile:mb-1">
              {isItemFolder ? getFolderIcon(item) : getFileIcon(item.name, item.type)}
            </div>

            {/* Name */}
            <p className="text-dark3 text-bodyMR font-medium text-center truncate w-full">
              {item.name}
            </p>

            {/* Date */}
            <p className={`text-bodyESM mt-0 ${isItemFolder ? 'text-dark6' : 'text-gray5'}`}>
              {formatDate(item.date)}
            </p>

            {/* Size */}
            <p className={`text-bodyESM mt-0 ${isItemFolder ? 'text-dark6' : 'text-gray5'}`}>
              {isItemFolder ? '-' : formatFileSize(item.size)}
            </p>
          </div>
          {/* Recursively render nested items */}
          {isItemFolder && isExpanded && childItems.length > 0 && renderNestedItemsIcons(childItems, indentLevel + 1)}
        </React.Fragment>
      );
    });
  };

  const onDeleteComplete = async () => {
    if (!selectedItem) return;
    
    try {
      const response = await deleteFiles([selectedItem.name]);

      if (response.data.success) {
        toast.success("File deleted successfully");
        try {
          await refreshFolderStructure();
        } catch (refreshError) {
          console.error("Error refreshing folder structure:", refreshError);
          toast.error("File deleted but failed to refresh structure");
        }
      } else {
        toast.error(response.data.error || "Error deleting file");
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error(error.response?.data?.error || "Error deleting file");
    }
  };



  const handleNavigateFolderContent = (folder) => {
    // If onFolderClick is provided (modal mode), use it
    if (onFolderClick) {
      onFolderClick(folder);
      return;
    }
    
    // Otherwise, use routing (page mode)
    const currentPath = params["*"] || "";
    // Remove trailing slash from folder.id if present
    const folderName = folder.id.replace(/\/+$/, '');
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    navigate(`/dashboard/my-files/${newPath}`);
  };

  const getBreadcrumbs = () => {
    // Use provided currentPath if in modal mode, otherwise use params
    const pathToUse = currentPath !== null ? currentPath : (params["*"] || "");
    const currentPathClean = pathToUse.trim().replace(/\/+$/, ""); // Remove trailing slashes
    if (!currentPathClean) {
      return [{ name: "Folders", path: "" }];
    }

    const pathParts = currentPathClean.split('/').filter(Boolean);
    const breadcrumbs = [{ name: "Folders", path: "" }];
    
    pathParts.forEach((part, index) => {
      const pathUpToHere = pathParts.slice(0, index + 1).join('/');
      breadcrumbs.push({
        name: part,
        path: pathUpToHere
      });
    });

    return breadcrumbs;
  };

  const handleBreadcrumbClick = (breadcrumbPath) => {
    // If onFolderClick is provided (modal mode), use it
    if (onFolderClick) {
      // For breadcrumbs, we need to navigate to the path, not the folder
      // Pass the path directly
      onFolderClick({ id: breadcrumbPath || "", name: breadcrumbPath || "Folders", isBreadcrumb: true });
      return;
    }
    
    // Otherwise, use routing (page mode)
    if (breadcrumbPath === "") {
      navigate('/dashboard/my-files');
    } else {
      navigate(`/dashboard/my-files/${breadcrumbPath}`);
    }
  };

  const getFileIcon = (fileName, fileType, className = "w-[80px] h-[60px]") => (
    <FileIcon fileName={fileName} fileType={fileType} className={className} />
  );

  const findStructureNode = (folder) => {
    const pathSource = folder?.path || folder?.fullPath || folder?.id || folder?.name;
    if (!pathSource) {
      return folder?.structure && typeof folder.structure === "object" ? folder.structure : null;
    }

    const normalizedPath = pathSource
      .toString()
      .replace(/^\/+/, "")
      .replace(/\/+$/, "");

    if (!normalizedPath) {
    return rootStructure;
    }

    const segments = normalizedPath.split("/");
    let currentNode = rootStructure;

    for (const segment of segments) {
      if (!currentNode || typeof currentNode !== "object") {
        return null;
      }

      const matchKey = Object.keys(currentNode).find(
        (key) => key.replace(/\/+$/, "") === segment,
      );

      if (!matchKey) {
        return null;
      }

      currentNode = currentNode[matchKey];
    }

    if (currentNode) {
      return currentNode;
    }

    if (folder?.structure && typeof folder.structure === "object") {
      return folder.structure;
    }

    return null;
  };

  const folderHasContent = (folder) => {
    if (!folder) return false;
    if (folder.hasFiles) return true;

    const structure = findStructureNode(folder);
    if (!structure || typeof structure !== "object") return false;

    return Object.entries(structure).some(([key, value]) => {
      if (
        key === "isFolder" ||
        key === "hasFiles" ||
        key === "path" ||
        key === "lastModified" ||
        key === "size" ||
        key === "etag"
      ) {
        return false;
      }

      if (!value || typeof value !== "object") {
        return false;
      }

      if (value.isFolder === true) {
        return folderHasContent({ structure: value });
      }

      if (value.isFolder === false) {
        return true;
      }

      return Object.keys(value).length > 0;
    });
  };

  const getFolderIcon = (folder, className = "w-[80px] h-[60px]") => {
    const hasContent = folderHasContent(folder);
    return (
    <FileIcon
      fileName={folder?.name || folder?.id || ""}
      fileType={hasContent ? "folderFilled" : "folderEmpty"}
      hasChildren={hasContent}
      className={className}
    />
    );
  };

  // Use provided currentPath if in modal mode, otherwise use params
  const pathToUse = currentPath !== null ? currentPath : (params["*"] || "");
  const currentPathDisplay = pathToUse.trim().replace(/\/+$/, "");
  const shouldShowBreadcrumb = currentPathDisplay !== "";

  // Combine folders and files into a single sorted array
  const allItems = useMemo(() => {
    // In both modes, only show direct files (not from subfolders)
    const filesToShow = files.filter(f => !f.folderPath);
    
    const combined = [
      ...folders.map(f => ({ ...f, itemType: 'folder' })),
      ...filesToShow.map(f => ({ ...f, itemType: 'file' }))
    ];
    
    // Sort based on sortBy option
    return combined.sort((a, b) => {
      switch (sortBy) {
        case "Name (A-Z)": {
          // Folders first, then files, both alphabetically
          if (a.itemType === 'folder' && b.itemType === 'file') return -1;
          if (a.itemType === 'file' && b.itemType === 'folder') return 1;
          return (a.name || a.id || '').localeCompare(b.name || b.id || '', undefined, { sensitivity: 'base' });
        }
        
        case "Type": {
          // Sort by type: folders first, then files by extension
          if (a.itemType === 'folder' && b.itemType === 'file') return -1;
          if (a.itemType === 'file' && b.itemType === 'folder') return 1;
          
          // For files, sort by extension
          if (a.itemType === 'file' && b.itemType === 'file') {
            const extA = (a.name || '').split('.').pop()?.toLowerCase() || '';
            const extB = (b.name || '').split('.').pop()?.toLowerCase() || '';
            if (extA !== extB) {
              return extA.localeCompare(extB);
            }
          }
          
          // If same type, sort by name
          return (a.name || a.id || '').localeCompare(b.name || b.id || '', undefined, { sensitivity: 'base' });
        }
        
        case "File Size": {
          // Sort by size (largest first)
          const sizeA = a.size || 0;
          const sizeB = b.size || 0;
          
          if (sizeB !== sizeA) {
            return sizeB - sizeA; // Descending order (largest first)
          }
          
          // If same size, folders first, then sort by name
          if (a.itemType === 'folder' && b.itemType === 'file') return -1;
          if (a.itemType === 'file' && b.itemType === 'folder') return 1;
          
          return (a.name || a.id || '').localeCompare(b.name || b.id || '', undefined, { sensitivity: 'base' });
        }
        
        case "Date (newest)": {
          // Sort by date (newest first)
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          
          if (dateB !== dateA) {
            return dateB - dateA; // Descending order (newest first)
          }
          
          // If same date, sort by name
          return (a.name || a.id || '').localeCompare(b.name || b.id || '', undefined, { sensitivity: 'base' });
        }
        
        case "Date (oldest)": {
          // Sort by date (oldest first)
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          
          if (dateA !== dateB) {
            return dateA - dateB; // Ascending order (oldest first)
          }
          
          // If same date, sort by name
          return (a.name || a.id || '').localeCompare(b.name || b.id || '', undefined, { sensitivity: 'base' });
        }
        
        default: {
          // Default: Name (A-Z) - folders first, then files
          if (a.itemType === 'folder' && b.itemType === 'file') return -1;
          if (a.itemType === 'file' && b.itemType === 'folder') return 1;
          return (a.name || a.id || '').localeCompare(b.name || b.id || '', undefined, { sensitivity: 'base' });
        }
      }
    });
  }, [folders, files, sortBy]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Hourglass
          visible={true}
          height={40}
          width={40}
          animationDuration={2000}
          ariaLabel="hourglass-loading"
          colors={["#FF5400", "#FFA500"]}
        />
      </div>
    );
  }

  return (
    <div className="p-6 mobile:p-4 space-y-8 px-0">
      {/* Header with breadcrumb and view mode buttons */}
      <div className="flex items-center justify-between mb-4">
        {shouldShowBreadcrumb && (
          <div className="flex items-center gap-2">
            <h3 className="text-dark4 text-bodyLB font-normal">
              {getBreadcrumbs().map((breadcrumb, index) => (
                <span key={index}>
                  <span
                    onClick={() => handleBreadcrumbClick(breadcrumb.path)}
                    className={`cursor-pointer hover:text-primary transition-colors ${
                      index === getBreadcrumbs().length - 1 ? 'text-dark4 font-medium' : 'text-dark5'
                    }`}
                  >
                    {breadcrumb.name}
                  </span>
                  {index < getBreadcrumbs().length - 1 && (
                    <span className="text-dark5 mx-2">/</span>
                  )}
                </span>
              ))}
            </h3>
          </div>
        )}
        
        {/* View Mode Buttons */}
        <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1 bg-white ml-auto">
          <button
            onClick={() => setViewMode('icons')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'icons' 
                ? 'bg-primary text-white' 
                : 'text-dark4 hover:bg-gray-100'
            }`}
            title="View as Icons"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <rect x="2" y="2" width="6" height="6" rx="1"/>
              <rect x="11" y="2" width="6" height="6" rx="1"/>
              <rect x="2" y="11" width="6" height="6" rx="1"/>
              <rect x="11" y="11" width="6" height="6" rx="1"/>
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'list' 
                ? 'bg-primary text-white' 
                : 'text-dark4 hover:bg-gray-100'
            }`}
            title="View as List"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <rect x="2" y="3" width="16" height="2" rx="1"/>
              <rect x="2" y="9" width="16" height="2" rx="1"/>
              <rect x="2" y="15" width="16" height="2" rx="1"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <ImagePreviewModal
          isOpen={!!previewImage}
          onClose={() => setPreviewImage(null)}
          image={previewImage}
        />
      )}

      {/* Unified Items Section */}
      {allItems.length > 0 && (
        <div>
          {viewMode === 'icons' && (
            <div className="grid grid-cols-2 text-dark4 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {renderNestedItemsIcons(allItems, 0)}
            </div>
          )}

          {/* Old icons mode code - keeping for reference but replaced with recursive function */}
          {false && viewMode === 'icons' && (
            <div className="grid grid-cols-2 text-dark4 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allItems.map((item) => {
                const isFolder = item.itemType === 'folder';
                
                return (
              <div
                key={item.id}
                className={`
                  relative flex flex-col items-center p-4 rounded-lg cursor-pointer
                  transition-all duration-200 group
                  ${selectedIds.includes(normalizeItemId(item)) ? "bg-blue-50 ring-2 ring-primary" : "bg-white hover:shadow-xl"}
                `}
                onClick={() => handleItemClick(item)}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  className={`
                    absolute top-2 left-2 rounded-3xl cursor-pointer border-gray-400
                    transition-opacity duration-200 mobile:w-4 mobile:h-4 w-5 h-5 border-c
                    ${selectedIds.includes(normalizeItemId(item)) ? "opacity-100" : "opacity-1 group-hover:opacity-100"}
                  `}
                  checked={selectedIds.includes(normalizeItemId(item))}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleCheckboxChange(item, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />

                {/* Three dots dropdown menu */}
                <div className="absolute top-2 right-2">
                  <button
                    className={`
                      text-dark4 hover:text-primary p-1 rounded
                      transition-opacity duration-200
                      ${dropdownOpen === item.id ? "opacity-100" : "opacity-1 group-hover:opacity-100"}
                    `}
                    onClick={(e) => handleDropdownToggle(item.id, e)}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <circle cx="10" cy="4" r="1.5"/>
                      <circle cx="10" cy="10" r="1.5"/>
                      <circle cx="10" cy="16" r="1.5"/>
                    </svg>
                  </button>

                  {dropdownOpen === item.id && (
                    <div ref={dropdownRef} className="absolute right-0 mt-2 w-44 desktop:w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      {isFolder ? (
                        <>
                          <button
                            className="w-full text-dark4 mobile:text-bodySR text-left px-3 mobile:px-2 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-t-lg "
                            onClick={(e) => handleDownload(item, e)}
                          >
                            <svg width="18" height="16" viewBox="0 0 22 18" fill="currentColor">
                              <path fillRule="evenodd" clipRule="evenodd" d="M18.5 18C19.2956 18 20.0587 17.6839 20.6213 17.1213C21.1839 16.5587 21.5 15.7956 21.5 15V6C21.5 5.20435 21.1839 4.44129 20.6213 3.87868C20.0587 3.31607 19.2956 3 18.5 3H13.121C12.9222 2.99983 12.7315 2.92069 12.591 2.78L10.47 0.66C10.2611 0.450888 10.0131 0.284971 9.74016 0.171727C9.46717 0.0584821 9.17454 0.000128741 8.879 0H3.5C2.70435 0 1.94129 0.316071 1.37868 0.87868C0.816071 1.44129 0.5 2.20435 0.5 3V15C0.5 15.7956 0.816071 16.5587 1.37868 17.1213C1.94129 17.6839 2.70435 18 3.5 18H18.5ZM11.75 7.5C11.75 7.30109 11.671 7.11032 11.5303 6.96967C11.3897 6.82902 11.1989 6.75 11 6.75C10.8011 6.75 10.6103 6.82902 10.4697 6.96967C10.329 7.11032 10.25 7.30109 10.25 7.5V11.69L8.53 9.97C8.38783 9.83752 8.19978 9.7654 8.00548 9.76882C7.81118 9.77225 7.62579 9.85097 7.48838 9.98838C7.35097 10.1258 7.27225 10.3112 7.26882 10.5055C7.2654 10.6998 7.33752 10.8878 7.47 11.03L10.47 14.03C10.6106 14.1705 10.8012 14.2493 11 14.2493C11.1988 14.2493 11.3894 14.1705 11.53 14.03L14.53 11.03C14.6037 10.9613 14.6628 10.8785 14.7038 10.7865C14.7448 10.6945 14.7668 10.5952 14.7686 10.4945C14.7704 10.3938 14.7518 10.2938 14.7141 10.2004C14.6764 10.107 14.6203 10.0222 14.549 9.95096C14.4778 9.87974 14.393 9.8236 14.2996 9.78588C14.2062 9.74816 14.1062 9.72963 14.0055 9.73141C13.9048 9.73318 13.8055 9.75523 13.7135 9.79622C13.6215 9.83721 13.5387 9.89631 13.47 9.97L11.75 11.69V7.5Z" fill="currentColor"/>
                            </svg>
                            <span>Download this folder</span>
                          </button>
                          <button
                            className="w-full text-dark4 mobile:text-bodySR text-left px-3 mobile:px-2 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-b-lg"
                            onClick={(e) => handleDeleteFolder(item, e)}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                            </svg>
                            <span>Delete this folder</span>
                          </button>
                        </>
                      ) : (
                        <>
                          {item.type === "image" && (
                          <button
                            className="w-full text-dark4 text-left px-3 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-t-lg"
                            onClick={(e) => handleDownload(item, e)}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                            </svg>
                            <span>Download this file</span>
                          </button>
                          )}
                          <button
                            className="w-full text-dark4 text-left px-3 py-2 hover:bg-primary hover:text-white flex items-center space-x-2 rounded-b-lg"
                            onClick={(e) => handleDelete(item, e)}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                            </svg>
                            <span>Delete this file</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Icon */}
                <div className="mb-3 mobile:mb-1">
                  {isFolder ? getFolderIcon(item) : getFileIcon(item.name, item.type)}
                </div>

                {/* Name */}
                <p className="text-dark3 text-bodyMR font-medium text-center truncate w-full">
                  {item.name}
                </p>

                {/* Date */}
                <p className={`text-bodyESM mt-0 ${isFolder ? 'text-dark6' : 'text-gray5'}`}>
                  {formatDate(item.date)}
                </p>

                {/* Size */}
                <p className={`text-bodyESM mt-0 ${isFolder ? 'text-dark6' : 'text-gray5'}`}>
                  {isFolder ? '-' : formatFileSize(item.size)}
                </p>
              </div>
                );
              })}
            </div>
          )}

          {viewMode === 'list' && (
            <>
              {/* Mobile List View */}
              <div className="md:hidden bg-white rounded-lg border border-gray-200">
                <div className="relative">
                  {renderNestedItemsListMobile(allItems, 0)}
                </div>
              </div>

              {/* Desktop List View */}
              <div className="hidden md:block bg-white rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-bodyMR text-dark4 font-semibold">Name</th>
                      <th className="text-left px-4 py-3 text-bodyMR text-dark4 font-semibold">Type</th>
                      <th className="text-left px-4 py-3 text-bodyMR text-dark4 font-semibold whitespace-nowrap">Date Created</th>
                      <th className="text-left px-4 py-3 text-bodyMR text-dark4 font-semibold">Size</th>
                      <th className="text-right px-4 py-3 text-bodyMR text-dark4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderNestedItemsList(allItems, 0)}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Old list mode code - keeping for reference but replaced with recursive function */}
          {false && viewMode === 'list' && (
            <div className="bg-white rounded-lg border border-gray-200">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-bodyMR text-dark4 font-semibold">Name</th>
                    <th className="text-left px-4 py-3 text-bodyMR text-dark4 font-semibold">Type</th>
                    <th className="text-left px-4 py-3 text-bodyMR text-dark4 font-semibold whitespace-nowrap">Date Created</th>
                    <th className="text-left px-4 py-3 text-bodyMR text-dark4 font-semibold">Size</th>
                    <th className="text-right px-4 py-3 text-bodyMR text-dark4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.map((item) => {
                    const isFolder = item.itemType === 'folder';
                    const isExpanded = isFolder && expandedFolders.has(item.id);
                    const folderFiles = isFolder && isExpanded && item.hasFiles ? getFilesFromFolder(item) : [];
                    
                    return (
                      <React.Fragment key={item.id}>
                        <tr
                          className={`
                            border-b border-gray-100 hover:bg-gray-50 cursor-pointer
                            ${selectedIds.includes(normalizeItemId(item)) ? "bg-blue-50" : ""}
                          `}
                          onClick={() => handleItemClick(item)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                className="rounded cursor-pointer border-gray-400 w-4 h-4"
                                checked={selectedIds.includes(normalizeItemId(item))}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleCheckboxChange(item, e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="flex items-center gap-2">
                                {/* Chevron for folders with files in list mode */}
                                {isFolder && item.hasFiles && viewMode === 'list' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleFolderExpand(item.id);
                                    }}
                                    className="text-dark4 hover:text-primary transition-colors"
                                  >
                                    <svg 
                                      width="16" 
                                      height="16" 
                                      viewBox="0 0 16 16" 
                                      fill="none"
                                      className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                    >
                                      <path 
                                        d="M6 4L10 8L6 12" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                )}
                                <div className="w-8 h-8 flex items-center justify-center">
                                  {isFolder
                                    ? getFolderIcon(item, "w-[25px] h-[22px]")
                                    : getFileIcon(item.name, item.type, "w-[25px] h-[22px]")}
                                </div>
                                <div className="flex flex-col min-w-0">
                                  <span className="text-bodyMR text-dark3 font-medium truncate max-w-[200px]">{item.name}</span>
                                  {!isFolder && item.folderPath && (
                                    <span className="text-bodyESM text-dark5 truncate max-w-[200px]">in {item.folderPath}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-bodyESM text-dark5 capitalize">
                            {formatItemTypeLabel(item)}
                          </td>
                          <td className="px-4 py-3 text-bodyESM text-dark5">
                            {formatDate(item.date)}
                          </td>
                          <td className="px-4 py-3 text-bodyESM text-dark5">
                            {isFolder ? '-' : formatFileSize(item.size)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {isFolder ? (
                                <>
                                  
                                  <button
                                    className="text-dark4 hover:text-primary p-1 rounded transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(item, e);
                                    }}
                                    title="Download this folder"
                                  >
                                    <svg width="18" height="16" viewBox="0 0 22 18" fill="currentColor">
                                      <path fillRule="evenodd" clipRule="evenodd" d="M18.5 18C19.2956 18 20.0587 17.6839 20.6213 17.1213C21.1839 16.5587 21.5 15.7956 21.5 15V6C21.5 5.20435 21.1839 4.44129 20.6213 3.87868C20.0587 3.31607 19.2956 3 18.5 3H13.121C12.9222 2.99983 12.7315 2.92069 12.591 2.78L10.47 0.66C10.2611 0.450888 10.0131 0.284971 9.74016 0.171727C9.46717 0.0584821 9.17454 0.000128741 8.879 0H3.5C2.70435 0 1.94129 0.316071 1.37868 0.87868C0.816071 1.44129 0.5 2.20435 0.5 3V15C0.5 15.7956 0.816071 16.5587 1.37868 17.1213C1.94129 17.6839 2.70435 18 3.5 18H18.5ZM11.75 7.5C11.75 7.30109 11.671 7.11032 11.5303 6.96967C11.3897 6.82902 11.1989 6.75 11 6.75C10.8011 6.75 10.6103 6.82902 10.4697 6.96967C10.329 7.11032 10.25 7.30109 10.25 7.5V11.69L8.53 9.97C8.38783 9.83752 8.19978 9.7654 8.00548 9.76882C7.81118 9.77225 7.62579 9.85097 7.48838 9.98838C7.35097 10.1258 7.27225 10.3112 7.26882 10.5055C7.2654 10.6998 7.33752 10.8878 7.47 11.03L10.47 14.03C10.6106 14.1705 10.8012 14.2493 11 14.2493C11.1988 14.2493 11.3894 14.1705 11.53 14.03L14.53 11.03C14.6037 10.9613 14.6628 10.8785 14.7038 10.7865C14.7448 10.6945 14.7668 10.5952 14.7686 10.4945C14.7704 10.3938 14.7518 10.2938 14.7141 10.2004C14.6764 10.107 14.6203 10.0222 14.549 9.95096C14.4778 9.87974 14.393 9.8236 14.2996 9.78588C14.2062 9.74816 14.1062 9.72963 14.0055 9.73141C13.9048 9.73318 13.8055 9.75523 13.7135 9.79622C13.6215 9.83721 13.5387 9.89631 13.47 9.97L11.75 11.69V7.5Z" fill="currentColor"/>
                                    </svg>
                                  </button>
                                  <button
                                    className="text-dark4 hover:text-red-500 p-1 rounded transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFolder(item, e);
                                    }}
                                    title="Delete this folder"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="#FF5400">
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="text-dark4 hover:text-primary p-1 rounded transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(item, e);
                                    }}
                                    title="Download this file"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                                    </svg>
                                  </button>
                                  <button
                                    className="text-dark4 hover:text-red-500 p-1 rounded transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(item, e);
                                    }}
                                    title="Delete this file"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="#FF5400">
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                      </tr>
                      {/* Render child files and folders when folder is expanded */}
                      {isFolder && isExpanded && folderFiles.map((childItem) => {
                        const isChildFolder = childItem.itemType === 'folder' || childItem.type === 'folder';
                        const isChildExpanded = isChildFolder && expandedFolders.has(childItem.id);
                        const childFolderFiles = isChildFolder && isChildExpanded && childItem.hasFiles ? getFilesFromFolder(childItem) : [];
                        const indentLevel = 1; // Base indent level for first level children
                        
                        return (
                          <React.Fragment key={childItem.id}>
                            <tr
                              className="border-b border-gray-100 hover:bg-gray-50 bg-gray-50/50"
                              onClick={() => {
                                if (isChildFolder) {
                                  // For child folders, navigate or expand
                                  if (viewMode === 'list' && childItem.hasFiles) {
                                    handleToggleFolderExpand(childItem.id);
                                  } else {
                                    handleNavigateFolderContent(childItem);
                                  }
                                } else {
                                  tryOpenPreview(childItem);
                                }
                              }}
                            >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3" style={{ paddingLeft: `${(indentLevel) * 2}rem` }}>
                                <input
                                  type="checkbox"
                                  className="rounded cursor-pointer border-gray-400 w-4 h-4"
                                  checked={selectedIds.includes(normalizeItemId(childItem))}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleCheckboxChange(childItem, e.target.checked);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="flex items-center gap-2">
                                  {isChildFolder && childItem.hasFiles && viewMode === 'list' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleFolderExpand(childItem.id);
                                      }}
                                      className="text-dark4 hover:text-primary transition-colors"
                                    >
                                      <svg 
                                        width="16" 
                                        height="16" 
                                        viewBox="0 0 16 16" 
                                        fill="none"
                                        className={`transition-transform duration-200 ${isChildExpanded ? 'rotate-90' : ''}`}
                                      >
                                        <path 
                                          d="M6 4L10 8L6 12" 
                                          stroke="currentColor" 
                                          strokeWidth="2" 
                                          strokeLinecap="round" 
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </button>
                                  )}
                                  <div className="w-8 h-8 flex items-center justify-center">
                                    {isChildFolder
                                      ? getFolderIcon(childItem, "w-[25px] h-[22px]")
                                      : getFileIcon(childItem.name, childItem.type, "w-[25px] h-[22px]")}
                                  </div>
                                  <div className="flex flex-col min-w-0">
                                    <span className="text-bodyMR text-dark3 font-medium truncate max-w-[200px]">{childItem.name}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-bodyESM text-dark5 capitalize">
                              {formatItemTypeLabel(childItem)}
                            </td>
                            <td className="px-4 py-3 text-bodyESM text-dark5">
                              {formatDate(childItem.date)}
                            </td>
                            <td className="px-4 py-3 text-bodyESM text-dark5">
                              {isChildFolder ? '-' : formatFileSize(childItem.size)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {isChildFolder ? (
                                  <>
                                    <button
                                      className="text-dark4 hover:text-primary p-1 rounded transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownload(childItem, e);
                                      }}
                                      title="Download this folder"
                                    >
                                      <svg width="18" height="16" viewBox="0 0 22 18" fill="currentColor">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M18.5 18C19.2956 18 20.0587 17.6839 20.6213 17.1213C21.1839 16.5587 21.5 15.7956 21.5 15V6C21.5 5.20435 21.1839 4.44129 20.6213 3.87868C20.0587 3.31607 19.2956 3 18.5 3H13.121C12.9222 2.99983 12.7315 2.92069 12.591 2.78L10.47 0.66C10.2611 0.450888 10.0131 0.284971 9.74016 0.171727C9.46717 0.0584821 9.17454 0.000128741 8.879 0H3.5C2.70435 0 1.94129 0.316071 1.37868 0.87868C0.816071 1.44129 0.5 2.20435 0.5 3V15C0.5 15.7956 0.816071 16.5587 1.37868 17.1213C1.94129 17.6839 2.70435 18 3.5 18H18.5ZM11.75 7.5C11.75 7.30109 11.671 7.11032 11.5303 6.96967C11.3897 6.82902 11.1989 6.75 11 6.75C10.8011 6.75 10.6103 6.82902 10.4697 6.96967C10.329 7.11032 10.25 7.30109 10.25 7.5V11.69L8.53 9.97C8.38783 9.83752 8.19978 9.7654 8.00548 9.76882C7.81118 9.77225 7.62579 9.85097 7.48838 9.98838C7.35097 10.1258 7.27225 10.3112 7.26882 10.5055C7.2654 10.6998 7.33752 10.8878 7.47 11.03L10.47 14.03C10.6106 14.1705 10.8012 14.2493 11 14.2493C11.1988 14.2493 11.3894 14.1705 11.53 14.03L14.53 11.03C14.6037 10.9613 14.6628 10.8785 14.7038 10.7865C14.7448 10.6945 14.7668 10.5952 14.7686 10.4945C14.7704 10.3938 14.7518 10.2938 14.7141 10.2004C14.6764 10.107 14.6203 10.0222 14.549 9.95096C14.4778 9.87974 14.393 9.8236 14.2996 9.78588C14.2062 9.74816 14.1062 9.72963 14.0055 9.73141C13.9048 9.73318 13.8055 9.75523 13.7135 9.79622C13.6215 9.83721 13.5387 9.89631 13.47 9.97L11.75 11.69V7.5Z" fill="currentColor"/>
                                      </svg>
                                    </button>
                                    <button
                                      className="text-dark4 hover:text-red-500 p-1 rounded transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteFolder(childItem, e);
                                      }}
                                      title="Delete this folder"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="#FF5400">
                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                      </svg>
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="text-dark4 hover:text-primary p-1 rounded transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownload(childItem, e);
                                      }}
                                      title="Download this file"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                                      </svg>
                                    </button>
                                    <button
                                      className="text-dark4 hover:text-red-500 p-1 rounded transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(childItem, e);
                                      }}
                                      title="Delete this file"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="#FF5400">
                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                      </svg>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          {/* Recursively render nested folder contents */}
                          {isChildFolder && isChildExpanded && childFolderFiles.map((nestedItem) => {
                            const isNestedFolder = nestedItem.itemType === 'folder' || nestedItem.type === 'folder';
                            const isNestedExpanded = isNestedFolder && expandedFolders.has(nestedItem.id);
                            const nestedFolderFiles = isNestedFolder && isNestedExpanded && nestedItem.hasFiles ? getFilesFromFolder(nestedItem) : [];
                            const nestedIndentLevel = indentLevel + 1;
                            
                            return (
                              <React.Fragment key={nestedItem.id}>
                                <tr
                                  className="border-b border-gray-100 hover:bg-gray-50 bg-gray-50/50"
                                  onClick={() => {
                                    if (isNestedFolder) {
                                      if (viewMode === 'list' && nestedItem.hasFiles) {
                                        handleToggleFolderExpand(nestedItem.id);
                                      } else {
                                        handleNavigateFolderContent(nestedItem);
                                      }
                                } else {
                                  tryOpenPreview(nestedItem);
                                    }
                                  }}
                                >
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-3" style={{ paddingLeft: `${nestedIndentLevel * 2}rem` }}>
                                      <input
                                        type="checkbox"
                                        className="rounded cursor-pointer border-gray-400 w-4 h-4"
                                        checked={selectedIds.includes(normalizeItemId(nestedItem))}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleCheckboxChange(nestedItem, e.target.checked);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <div className="flex items-center gap-2">
                                        {isNestedFolder && nestedItem.hasFiles && viewMode === 'list' && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleToggleFolderExpand(nestedItem.id);
                                            }}
                                            className="text-dark4 hover:text-primary transition-colors"
                                          >
                                            <svg 
                                              width="16" 
                                              height="16" 
                                              viewBox="0 0 16 16" 
                                              fill="none"
                                              className={`transition-transform duration-200 ${isNestedExpanded ? 'rotate-90' : ''}`}
                                            >
                                              <path 
                                                d="M6 4L10 8L6 12" 
                                                stroke="currentColor" 
                                                strokeWidth="2" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </button>
                                        )}
                                        <div className="w-8 h-8 flex items-center justify-center">
                                          {isNestedFolder
                                            ? getFolderIcon(nestedItem, "w-[25px] h-[22px]")
                                            : getFileIcon(nestedItem.name, nestedItem.type, "w-[25px] h-[22px]")}
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                          <span className="text-bodyMR text-dark3 font-medium truncate max-w-[200px]">{nestedItem.name}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-bodyESM text-dark5 capitalize">
                                    {formatItemTypeLabel(nestedItem)}
                                  </td>
                                  <td className="px-4 py-3 text-bodyESM text-dark5">
                                    {formatDate(nestedItem.date)}
                                  </td>
                                  <td className="px-4 py-3 text-bodyESM text-dark5">
                                    {isNestedFolder ? '-' : formatFileSize(nestedItem.size)}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      {isNestedFolder ? (
                                        <>
                                          <button
                                            className="text-dark4 hover:text-primary p-1 rounded transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownload(nestedItem, e);
                                            }}
                                            title="Download this folder"
                                          >
                                            <svg width="18" height="16" viewBox="0 0 22 18" fill="currentColor">
                                              <path fillRule="evenodd" clipRule="evenodd" d="M18.5 18C19.2956 18 20.0587 17.6839 20.6213 17.1213C21.1839 16.5587 21.5 15.7956 21.5 15V6C21.5 5.20435 21.1839 4.44129 20.6213 3.87868C20.0587 3.31607 19.2956 3 18.5 3H13.121C12.9222 2.99983 12.7315 2.92069 12.591 2.78L10.47 0.66C10.2611 0.450888 10.0131 0.284971 9.74016 0.171727C9.46717 0.0584821 9.17454 0.000128741 8.879 0H3.5C2.70435 0 1.94129 0.316071 1.37868 0.87868C0.816071 1.44129 0.5 2.20435 0.5 3V15C0.5 15.7956 0.816071 16.5587 1.37868 17.1213C1.94129 17.6839 2.70435 18 3.5 18H18.5ZM11.75 7.5C11.75 7.30109 11.671 7.11032 11.5303 6.96967C11.3897 6.82902 11.1989 6.75 11 6.75C10.8011 6.75 10.6103 6.82902 10.4697 6.96967C10.329 7.11032 10.25 7.30109 10.25 7.5V11.69L8.53 9.97C8.38783 9.83752 8.19978 9.7654 8.00548 9.76882C7.81118 9.77225 7.62579 9.85097 7.48838 9.98838C7.35097 10.1258 7.27225 10.3112 7.26882 10.5055C7.2654 10.6998 7.33752 10.8878 7.47 11.03L10.47 14.03C10.6106 14.1705 10.8012 14.2493 11 14.2493C11.1988 14.2493 11.3894 14.1705 11.53 14.03L14.53 11.03C14.6037 10.9613 14.6628 10.8785 14.7038 10.7865C14.7448 10.6945 14.7668 10.5952 14.7686 10.4945C14.7704 10.3938 14.7518 10.2938 14.7141 10.2004C14.6764 10.107 14.6203 10.0222 14.549 9.95096C14.4778 9.87974 14.393 9.8236 14.2996 9.78588C14.2062 9.74816 14.1062 9.72963 14.0055 9.73141C13.9048 9.73318 13.8055 9.75523 13.7135 9.79622C13.6215 9.83721 13.5387 9.89631 13.47 9.97L11.75 11.69V7.5Z" fill="currentColor"/>
                                            </svg>
                                          </button>
                                          <button
                                            className="text-dark4 hover:text-red-500 p-1 rounded transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteFolder(nestedItem, e);
                                            }}
                                            title="Delete this folder"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="#FF5400">
                                              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                            </svg>
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            className="text-dark4 hover:text-primary p-1 rounded transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownload(nestedItem, e);
                                            }}
                                            title="Download this file"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                                              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                                            </svg>
                                          </button>
                                          <button
                                            className="text-dark4 hover:text-red-500 p-1 rounded transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDelete(nestedItem, e);
                                            }}
                                            title="Delete this file"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="#FF5400">
                                              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                            </svg>
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {/* Recursively render deeper nested items if needed */}
                                {isNestedFolder && isNestedExpanded && nestedFolderFiles.map((deepNestedItem) => {
                                  const isDeepNestedFolder = deepNestedItem.itemType === 'folder' || deepNestedItem.type === 'folder';
                                  const deepNestedIndentLevel = nestedIndentLevel + 1;
                                  
                                  return (
                                    <tr
                                      key={deepNestedItem.id}
                                      className="border-b border-gray-100 hover:bg-gray-50 bg-gray-50/50"
                                      onClick={() => {
                                        if (isDeepNestedFolder) {
                                          if (viewMode === 'list' && deepNestedItem.hasFiles) {
                                            handleToggleFolderExpand(deepNestedItem.id);
                                          } else {
                                            handleNavigateFolderContent(deepNestedItem);
                                          }
                                } else {
                                  tryOpenPreview(deepNestedItem);
                                        }
                                      }}
                                    >
                                      <td className="px-4 py-3">
                                        <div className="flex items-center gap-3" style={{ paddingLeft: `${deepNestedIndentLevel * 2}rem` }}>
                                          <input
                                            type="checkbox"
                                            className="rounded cursor-pointer border-gray-400 w-4 h-4"
                                            checked={selectedIds.includes(normalizeItemId(deepNestedItem))}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handleCheckboxChange(deepNestedItem, e.target.checked);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <div className="flex items-center gap-2">
                                            {isDeepNestedFolder && deepNestedItem.hasFiles && viewMode === 'list' && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleToggleFolderExpand(deepNestedItem.id);
                                                }}
                                                className="text-dark4 hover:text-primary transition-colors"
                                              >
                                                <svg 
                                                  width="16" 
                                                  height="16" 
                                                  viewBox="0 0 16 16" 
                                                  fill="none"
                                                  className={`transition-transform duration-200 ${expandedFolders.has(deepNestedItem.id) ? 'rotate-90' : ''}`}
                                                >
                                                  <path 
                                                    d="M6 4L10 8L6 12" 
                                                    stroke="currentColor" 
                                                    strokeWidth="2" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                              </button>
                                            )}
                                      <div className="w-8 h-8 flex items-center justify-center">
                                        {isDeepNestedFolder
                                          ? getFolderIcon(deepNestedItem, "w-[25px] h-[22px]")
                                          : getFileIcon(deepNestedItem.name, deepNestedItem.type, "w-[25px] h-[22px]")}
                                            </div>
                                            <div className="flex flex-col min-w-0">
                                              <span className="text-bodyMR text-dark3 font-medium truncate max-w-[200px]">{deepNestedItem.name}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                  <td className="px-4 py-3 text-bodyESM text-dark5 capitalize">
                                    {formatItemTypeLabel(deepNestedItem)}
                                      </td>
                                      <td className="px-4 py-3 text-bodyESM text-dark5">
                                        {formatDate(deepNestedItem.date)}
                                      </td>
                                      <td className="px-4 py-3 text-bodyESM text-dark5">
                                        {isDeepNestedFolder ? '-' : formatFileSize(deepNestedItem.size)}
                                      </td>
                                      <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                          {isDeepNestedFolder ? (
                                            <>
                                              <button
                                                className="text-dark4 hover:text-primary p-1 rounded transition-colors"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDownload(deepNestedItem, e);
                                                }}
                                                title="Download this folder"
                                              >
                                                <svg width="18" height="16" viewBox="0 0 22 18" fill="currentColor">
                                                  <path fillRule="evenodd" clipRule="evenodd" d="M18.5 18C19.2956 18 20.0587 17.6839 20.6213 17.1213C21.1839 16.5587 21.5 15.7956 21.5 15V6C21.5 5.20435 21.1839 4.44129 20.6213 3.87868C20.0587 3.31607 19.2956 3 18.5 3H13.121C12.9222 2.99983 12.7315 2.92069 12.591 2.78L10.47 0.66C10.2611 0.450888 10.0131 0.284971 9.74016 0.171727C9.46717 0.0584821 9.17454 0.000128741 8.879 0H3.5C2.70435 0 1.94129 0.316071 1.37868 0.87868C0.816071 1.44129 0.5 2.20435 0.5 3V15C0.5 15.7956 0.816071 16.5587 1.37868 17.1213C1.94129 17.6839 2.70435 18 3.5 18H18.5ZM11.75 7.5C11.75 7.30109 11.671 7.11032 11.5303 6.96967C11.3897 6.82902 11.1989 6.75 11 6.75C10.8011 6.75 10.6103 6.82902 10.4697 6.96967C10.329 7.11032 10.25 7.30109 10.25 7.5V11.69L8.53 9.97C8.38783 9.83752 8.19978 9.7654 8.00548 9.76882C7.81118 9.77225 7.62579 9.85097 7.48838 9.98838C7.35097 10.1258 7.27225 10.3112 7.26882 10.5055C7.2654 10.6998 7.33752 10.8878 7.47 11.03L10.47 14.03C10.6106 14.1705 10.8012 14.2493 11 14.2493C11.1988 14.2493 11.3894 14.1705 11.53 14.03L14.53 11.03C14.6037 10.9613 14.6628 10.8785 14.7038 10.7865C14.7448 10.6945 14.7668 10.5952 14.7686 10.4945C14.7704 10.3938 14.7518 10.2938 14.7141 10.2004C14.6764 10.107 14.6203 10.0222 14.549 9.95096C14.4778 9.87974 14.393 9.8236 14.2996 9.78588C14.2062 9.74816 14.1062 9.72963 14.0055 9.73141C13.9048 9.73318 13.8055 9.75523 13.7135 9.79622C13.6215 9.83721 13.5387 9.89631 13.47 9.97L11.75 11.69V7.5Z" fill="currentColor"/>
                                                </svg>
                                              </button>
                                              <button
                                                className="text-dark4 hover:text-red-500 p-1 rounded transition-colors"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteFolder(deepNestedItem, e);
                                                }}
                                                title="Delete this folder"
                                              >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="#FF5400">
                                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                                  <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                                </svg>
                                              </button>
                                            </>
                                          ) : (
                                            <>
                                              <button
                                                className="text-dark4 hover:text-primary p-1 rounded transition-colors"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDownload(deepNestedItem, e);
                                                }}
                                                title="Download this file"
                                              >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                                                  <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                                                </svg>
                                              </button>
                                              <button
                                                className="text-dark4 hover:text-red-500 p-1 rounded transition-colors"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDelete(deepNestedItem, e);
                                                }}
                                                title="Delete this file"
                                              >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="#FF5400">
                                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                                  <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                                </svg>
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                          </React.Fragment>
                        );
                      })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {allItems.length === 0 && (
        <div className="flex flex-col items-center justify-center h-full py-28 bg-white rounded-lg">
          <img src={`${process.env.PUBLIC_URL}/Folder-empty.svg`} className="w-20 h-20 mb-6 opacity-90" alt="folder" />
          <h3 className="text-dark5 text-h6 font-semibold mb-2">No files or folders</h3>
          <p className="text-dark5 text-bodyMR mt-2">This folder is empty</p>
        </div>
      )}

      {/* Modals */}
      <ModalDeleteFile
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        item={selectedItem}
        onDelete={onDeleteComplete}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default ContentFiles;